'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import posthog from 'posthog-js';
import { useAuth } from '@/contexts/AuthContext';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            nonce?: string;
            use_fedcm_for_prompt?: boolean;
            auto_select?: boolean;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: 'standard' | 'icon';
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              logo_alignment?: 'left' | 'center';
              width?: number;
              locale?: string;
            },
          ) => void;
          prompt: () => void;
          cancel: () => void;
        };
      };
    };
  }
}

const CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID ?? '';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

// Load the Google Identity Services script on demand (only where a sign-in button
// renders) instead of globally in the root layout, where it added ~96KB of JS to
// every page including the landing. Idempotent across button instances.
const ensureGisScript = () => {
  if (document.querySelector(`script[src="${GIS_SRC}"]`)) return;
  const s = document.createElement('script');
  s.src = GIS_SRC;
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
};

const generateNonce = async (): Promise<{ raw: string; hashed: string }> => {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const raw = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  const hashed = Array.from(new Uint8Array(hashBuffer), (b) => b.toString(16).padStart(2, '0')).join('');
  return { raw, hashed };
};

interface Props {
  mode?: 'signin' | 'signup';
  onError?: (message: string) => void;
}

export function GoogleSignInButton({ mode = 'signin', onError }: Props) {
  const { signInWithGoogle } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const rawNonceRef = useRef<string | null>(null);
  const [ready, setReady] = useState(false);

  const handleCredential = useCallback(
    async (response: { credential: string }) => {
      const nonce = rawNonceRef.current;
      if (!nonce) return;
      const { error, isNewUser } = await signInWithGoogle(response.credential, nonce);
      if (error) {
        onError?.(error);
        return;
      }
      // Decide by whether the account is actually new, not by which page the
      // button is on: new users signing in from /login were counted as sign-ins.
      posthog.capture(isNewUser ? 'user_signed_up' : 'user_signed_in_google', { method: 'google', mode });
    },
    [mode, onError, signInWithGoogle],
  );

  useEffect(() => {
    if (!CLIENT_ID) {
      console.warn('NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID is not set — Google sign-in button will not render.');
      return;
    }

    let cancelled = false;

    const init = async () => {
      const { raw, hashed } = await generateNonce();
      if (cancelled) return;
      rawNonceRef.current = raw;

      ensureGisScript();

      const waitForGis = () =>
        new Promise<void>((resolve) => {
          if (window.google?.accounts?.id) return resolve();
          const interval = setInterval(() => {
            if (window.google?.accounts?.id) {
              clearInterval(interval);
              resolve();
            }
          }, 50);
        });

      await waitForGis();
      if (cancelled || !containerRef.current) return;

      window.google!.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: handleCredential,
        nonce: hashed,
        use_fedcm_for_prompt: true,
      });

      window.google!.accounts.id.renderButton(containerRef.current, {
        type: 'standard',
        theme: 'filled_black',
        size: 'large',
        text: mode === 'signup' ? 'signup_with' : 'continue_with',
        shape: 'pill',
        logo_alignment: 'left',
        width: 360,
      });

      setReady(true);
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [handleCredential, mode]);

  return (
    <div className="flex justify-center mb-6 min-h-[44px]">
      <div ref={containerRef} style={{ colorScheme: 'light', opacity: ready ? 1 : 0 }} />
    </div>
  );
}
